import React, {
	useRef,
	useState,
	useMemo,
	useCallback,
	useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import Chart, { ChartLegendLabelItem } from 'chart.js';
import { ILinechartDataWithCustomLabels } from 'models';
import { IServiceStat } from 'modules/admin-panel/pages/results/models';
import { HistogramChartTooltip } from 'components/histogram-chart/HistogramChartTooltip';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import 'components/histogram-chart/histogram-chart.styles.scss';
import { generateLegendLabels } from '../../modules/admin-panel/pages/results/helpers';

const getDataSet = (
	serviceStats?: IServiceStat[],
	datasetColumnColors?: string[],
	t?: (key: string) => string,
) => {
	const chartData: ILinechartDataWithCustomLabels = {
		labels: [],
		datasets: [
			{
				data: [],
				backgroundColor: datasetColumnColors,
				maxBarThickness: 48,
				datalabels: {
					fontSize: '20',
					anchor: 'end',
					align: 'top',
					color: 'black',
				},
			},
		],
	};
	const countForZeroMark =
		serviceStats?.[0].mark === 0 ? serviceStats?.[0].count.toString() : '1';

	const length = (serviceStats?.length || 1) - 1;

	serviceStats?.forEach((stat, i) => {
		let chartLabel = '';
		if (stat.mark === 0) {
			if (t) {
				chartLabel = t('tooltip.not_rated');
			}
		} else {
			const label = `${i}/${length}`;
			let colorLabel = '';
			if (datasetColumnColors && datasetColumnColors[i] && t) {
				if (datasetColumnColors[i] === '#2e7d32') {
					colorLabel = t('tooltip.positive');
				} else if (datasetColumnColors[i] === '#f9a825') {
					colorLabel = t('tooltip.neutral');
				} else if (datasetColumnColors[i] === '#c62828') {
					colorLabel = t('tooltip.negative');
				}
			}
			chartLabel = `${colorLabel} \n ${label}`;
		}
		chartData.labels.push(chartLabel);
		chartData.datasets[0].data.push(stat.count);
	});
	return chartData;
};

interface IHistogramProps {
	wrapperClass?: string;
	// height?: string;
	dataSet?: IServiceStat[];
	getYLabels?: (
		value: number | string,
		index: number,
		values: number[] | string[],
	) => string | null | undefined;
	getXLabels?: (
		value: number | string,
		index: number,
		values: number[] | string[],
	) => string | null | undefined;
	min?: number;
	height?: string;
	width?: string;
	serviceRatingLevels?: Record<number, string>;
	columnColors: string[];
	getLegendLabels?: () => ChartLegendLabelItem[];
}

let myChart: Chart;

// myChart.options.scale?.ticks?.maxTicksLimit
export const HistogramChart = ({
	wrapperClass,
	height,
	width,
	dataSet,
	getYLabels,
	getXLabels,
	min,
	serviceRatingLevels,
	columnColors,
	getLegendLabels,
}: IHistogramProps) => {
	const { t } = useTranslation('results');
	const ref = useRef<HTMLCanvasElement>(null);
	const tooltip = useRef<HTMLDivElement>(null);
	const [tooltipInfo, setTooltipInfo] = useState({
		title: '',
		value: '',
	});

	const updatedColumnColors = columnColors ? ['#808080', ...columnColors] : [];
	const data = useMemo(() => getDataSet(dataSet, updatedColumnColors, t), [
		dataSet,
		updatedColumnColors,
		t,
	]);

	const max = useMemo(() => {
		const maxVal = Math.max(...data.datasets[0].data);

		return maxVal > 10 ? Math.ceil(maxVal / 10) * 10 : 10;
	}, [data]);

	const customTooltip = useCallback((tooltipModel: Chart.ChartTooltipModel) => {
		if (tooltip.current && ref.current) {
			if (tooltip.current.style.display === 'flex') {
				tooltip.current.style.display = 'none';
				return;
			}

			tooltip.current.style.display = 'flex';
			tooltip.current.style.bottom = `${
				(parseInt(ref.current.style.height, 10) - tooltipModel.caretY) /
					(parseInt(ref.current.style.height, 10) / ref.current.offsetHeight) +
				50
			}px`;
			tooltip.current.style.left = `${tooltipModel.caretX - 120}px`;
		}
	}, []);

	const createChartOptions = (countedSize: number) =>
		({
			type: 'bar',
			data,
			plugins: [ChartDataLabels],
			options: {
				legend: {
					display: !!getLegendLabels,
					position: 'bottom' as 'bottom',
					labels: {
						generateLabels: getLegendLabels || generateLegendLabels(t),
						fontSize: 16,
						padding: dataSet?.length! > 5 ? 40 : 20,
						usePointStyle: true,
					},
				},
				tooltips: {
					custom: customTooltip,
					enabled: false,
					callbacks: {
						label() {
							return '';
						},
						title: (item, chartData) => {
							setTooltipInfo((prev) => ({
								...prev,
								title: t(`${item[0].xLabel}`),
							}));
							return '';
						},
						footer: (item, chartData) => {
							setTooltipInfo((prev) => ({
								...prev,
								value: `${item[0].value} `,
							}));
							return '';
						},
					},
				},
				scales: {
					xAxes: [
						{
							ticks: {
								callback: (val) => {
									const stringValue = val.toString().trim();

									const parts = stringValue.split(' ');

									if (parts.length > 2) {
										const word = parts[0];
										const numbers = parts[2];

										return [word, numbers];
									}
									return stringValue;
								},
								fontSize: 16,
								fontColor: 'black',
							},
							gridLines: {
								borderDash: [5, 15],
								display: false,
								drawBorder: false,
							},
						},
					],
					yAxes: [
						{
							ticks: {
								min:
									min === undefined
										? Math.min(...data.datasets[0].data)
										: min || 0,
								max,
								callback:
									typeof getYLabels === 'function' ? getXLabels : (val) => val,
								maxTicksLimit: 10,
								fontSize: 16,
								fontColor: 'black',
							},
							gridLines: {
								borderDash: [5, 15],
								drawBorder: false,
								zeroLineColor: 'rgba(0, 7, 51, 0.08)',
							},
						},
					],
				},
			},
		} as Chart.ChartConfiguration);

	useEffect(() => {
		if (ref && ref.current && dataSet) {
			if (myChart && myChart.destroy) {
				myChart.destroy();
			}

			const w = window.innerWidth;
			let countedSize = w / 62;
			if (countedSize > 20) {
				countedSize = 16;
			}
			if (countedSize < 12) {
				countedSize = 12;
			}

			myChart = new Chart(ref.current, createChartOptions(countedSize));
		}
	}, [dataSet, ref]);

	return (
		<div className={classNames('histogram-chart', wrapperClass)}>
			<HistogramChartTooltip ref={tooltip} {...tooltipInfo} />
			<canvas
				className="histogram__canvas"
				ref={ref}
				height={height}
				width={width}
			/>
		</div>
	);
};
